import {
    LayoutDashboard, PackageSearch, BookOpenCheck, CreditCard, PersonStanding,
    Blocks, Logs, Castle, MicVocal, ShieldCheck, BadgeInfo,
    Briefcase, Languages, MapPin, Wifi, TreePine, Plane, Eye,
    User, Users, Tent, Shield, MessageCircle, Mail, Star, UserCheck
} from "lucide-react";

export const PERMISSION_STATE = Object.freeze({
    UNKNOWN: 'UNKNOWN',
    LOADING: 'LOADING',
    ALLOWED: 'ALLOWED',
    DENIED: 'DENIED'
});

export const ICON_MAP = {
    "Bookings": BookOpenCheck,
    "Admin Management": Shield,
    "User Management": Users,
    "Join With Us": UserCheck,
    "Host Applications": UserCheck,
    "Retreat Management": Tent,
    "Retreat Configuration": Blocks,
    "Content Management": Logs,
    "Reviews": Star,
    "Finance": CreditCard,
    "Support": Mail,
    "Master Data": Languages,
    "Dashboard": LayoutDashboard,
    "Users": User,
    "Hosts": Users,
    "Affiliates": PersonStanding,
    "Admin Users": Shield,
    "Profile": User,
    "Roles & Permissions": ShieldCheck,
    "Menu Permissions": BadgeInfo,
    "Menu Access": BadgeInfo,
    "Retreats": Tent,
    "Category Management": PackageSearch,
    "Hubs Locations": MapPin,
    "Hub Locations": MapPin,
    "Payment Transactions": CreditCard,
    "Payment Accounts": CreditCard,
    "Payment Methods": CreditCard,
    "Careers Management": Briefcase,
    "Press Management": MicVocal,
    "Blogs Management": Logs,
    "Blog Content": Logs,
    "Affiliate Commission": CreditCard,
    "Taxes and Fees": CreditCard,
    "Members": Blocks,
    "Facilitators": PersonStanding,
    "Centers": Castle,
    "Organizers": PersonStanding,
    "Retreat Enquiries": Mail,
    "Content Hub": Blocks,
    "Enquiries": Mail,
    "Review Management": Star,
    "Languages": Languages,
    "Featured Logos": Eye,
    "Testimonials": MessageCircle,
    "Contact Us": Mail,
    "CMS Tools": Shield,
    "Cancellation Policy Types": ShieldCheck,
    "Cancellation Policy": ShieldCheck,
    "FAQs Types": BadgeInfo,
    "Faqs Types": BadgeInfo,
    "FAQs": BadgeInfo,
    "Faqs": BadgeInfo,
    "Faqs Q&A": BadgeInfo,
    "Country Codes": MapPin,
    "Founders": User,
    "Social Media": MicVocal,
    "Atmosphere": TreePine,
    "Features": Wifi,
    "Packages": PackageSearch,
    "Not Include Packages": PackageSearch,
    "Facilities": Castle,
    "Surrounding Areas": MapPin,
    "Facilities Options": Logs,
    "Things in Area": TreePine,
    "Airports": Plane,
    "Visibility Plans": ShieldCheck,
    "Visibility Factors": ShieldCheck,
    "Addons": Blocks,
    "Meals": Briefcase,
    "Drinks": Briefcase,
    "Diet Options": Briefcase,
    "Review Questions": BadgeInfo,
};

// Normalize path for consistent comparison
export const normalizePath = (path) => {
    if (!path) return '/';
    // Strip query and hash
    const clean = path.split('?')[0].split('#')[0];
    const withSlash = clean.startsWith('/') ? clean : `/${clean}`;
    return withSlash.toLowerCase().replace(/\/+$/, '') || '/';
};

// Strip singular/plural 's' for base comparison
export const stripPlural = (p) => p.replace(/\/([a-z0-9_-]+)s(\/|$)/g, '/$1$2');

// Known route aliases and sub-pages
export const ROUTE_ALIASES = {
    '/retreat': ['/retreats', '/retreat/view-details', '/retreat/bookings', '/user-retreats'],
    '/retreats': ['/retreat', '/retreat/view-details', '/retreat/bookings', '/user-retreats'],
    '/booking': ['/bookings', '/user-bookings', '/bookings/view-details', '/bookings/create'],
    '/bookings': ['/booking', '/user-bookings', '/bookings/view-details', '/bookings/create'],
    '/bookings/refund-requests': ['/refund-requests', '/refund-request'],
    '/refund-requests': ['/bookings/refund-requests', '/refund-request'],
    '/user': ['/users', '/user-management', '/user-management/users', '/user-details', '/user/view-details'],
    '/users': ['/user', '/user-management', '/user-management/users', '/user-details', '/user/view-details'],
    '/admin-users': ['/admin-user'],
    '/host': ['/hosts', '/hosts-and-partners', '/hosts/kyc', '/hosts/profile'],
    '/hosts': ['/host', '/hosts-and-partners', '/hosts/kyc', '/hosts/profile'],
    '/hosts-and-partners': ['/hosts', '/host', '/hosts/kyc', '/hosts/profile'],
    '/host-applications': ['/host-application', '/become-hosts', '/become-host'],
    '/join-with-us': ['/host-application', '/become-hosts', '/become-host'],
    '/become-hosts': ['/host-applications', '/host-application'],
    '/category-management': ['/add-category', '/categories', '/category'],
    '/menu-permissions': ['/menu-permissions/add', '/menu-permissions/edit'],
    '/roles-permissions': ['/roles-permissions/add', '/roles-permissions/edit'],
    '/menu-access': [],
    '/payment-accounts': ['/payment-methods', '/payments'],
    '/payment-methods': ['/payment-accounts', '/payments'],
    '/press-management': ['/press-management/add', '/press-management/edit', '/press-management/news'],
    '/blogs-management': ['/blogs-management/add', '/blogs-management/edit', '/blogs-management/categories'],
    '/affiliate': ['/affiliates', '/affiliate/links', '/affiliates/links', '/affiliate/ledger', '/affiliates/ledger'],
    '/affiliates': ['/affiliate', '/affiliate/links', '/affiliates/links', '/affiliate/ledger', '/affiliates/ledger'],
    '/hubs-locations': ['/hub-locations', '/hubs-locations/add', '/hubs-locations/edit', '/hubs-locations/view-details'],
    '/review-management': ['/review-management/questions', '/review-management/reviews', '/reviews'],
    '/retreat-enquiries': ['/retreat-enquiry', '/retreat-enquiries/view-details'],
    '/add-retreat/atmosphere': ['/atmosphere', '/bookings/dd-retreat/atmosphere'],
    '/content-hub/faqs-types': ['/content-hub/faqs'],
    '/content-hub/faqs': ['/content-hub/faqs-types'],
};

// Normalize permissions object to standard format
export const normalizePermissions = (item) => ({
    view: !!item.view || !!item.read,
    create: !!item.create || !!item.add,
    read: !!item.read || !!item.view,
    edit: !!item.edit || !!item.update,
    remove: !!item.remove || !!item.delete,
    add: !!item.add || !!item.create,
    update: !!item.update || !!item.edit,
    delete: !!item.delete || !!item.remove
});

// Check if item path matches or covers current path
export const isPathActive = (itemPath, currentPath) => {
    if (!itemPath || !currentPath) return false;

    const item = normalizePath(itemPath);
    const current = normalizePath(currentPath);

    if (item === current) return true;

    // 1. Boundary rule for Bookings vs Refund Requests:
    // Main Bookings (/bookings) must never match Refund Requests (/bookings/refund-requests) or its subpaths
    // But /bookings/create should match /bookings
    if ((item === '/bookings' || item === '/booking') &&
        (current === '/bookings/refund-requests' || current.startsWith('/bookings/refund-requests/'))) {
        return false;
    }
    // Refund Requests must never match main Bookings or unrelated booking subpaths (e.g. view-details, create)
    if ((item === '/bookings/refund-requests' || item === '/refund-requests') &&
        (current === '/bookings' || current === '/booking' || current.startsWith('/bookings/view-details') || current === '/bookings/create')) {
        return false;
    }

    // 2. Boundary rule for Admin Management distinct sibling child routes:
    // Admin Users, Roles & Permissions, Menu Permissions, and Menu Access must never cross-match
    const adminManagementItems = ['/admin-users', '/roles-permissions', '/menu-permissions', '/menu-access'];
    if (adminManagementItems.includes(item)) {
        const currentSibling = adminManagementItems.find(sib => sib === current || current.startsWith(`${sib}/`));
        if (currentSibling && currentSibling !== item) {
            return false;
        }
    }

    // 3. Boundary rule for Content Hub FAQ routes:
    // /content-hub/faqs-types and /content-hub/faqs should cross-match (they are sibling routes)
    const faqRoutes = ['/content-hub/faqs-types', '/content-hub/faqs'];
    if (faqRoutes.includes(item)) {
        const faqSibling = faqRoutes.find(r => r === current);
        if (faqSibling) {
            return true;
        }
    }

    if (current.startsWith(`${item}/`)) return true;

    const itemStem = stripPlural(item);
    const currentStem = stripPlural(current);

    if (itemStem === currentStem) return true;
    if (currentStem.startsWith(`${itemStem}/`)) return true;

    if (ROUTE_ALIASES[item] && ROUTE_ALIASES[item].some(alias => alias === current || current.startsWith(`${alias}/`))) {
        return true;
    }

    if (ROUTE_ALIASES[current] && ROUTE_ALIASES[current].some(alias => alias === item || item.startsWith(`${alias}/`))) {
        return true;
    }

    return false;
};

// Build tree and pathMap from dynamic menus
export const buildMenuTreeAndPathMap = (dynamicMenus) => {
    if (!dynamicMenus || !Array.isArray(dynamicMenus) || dynamicMenus.length === 0) {
        return { tree: [], pathMap: {} };
    }

    const tree = [];
    const pMap = {};
    const isGroupedFormat = Array.isArray(dynamicMenus[0]?.menuItems);

    if (isGroupedFormat) {
        dynamicMenus.forEach(group => {
            const groupName = group.name || "";
            const allItems = group.menuItems || [];
            const visibleItems = allItems.filter(item => item.view && item.isActive !== false);

            // Populate pMap for ALL items in group (even if view is false, so permission system knows)
            allItems.forEach(item => {
                const path = item.slug || item.path || "";
                if (!path) return;
                const normalizedPath = normalizePath(path);
                const node = {
                    id: item.id,
                    menuId: item.menuId || item.id,
                    text: item.name || groupName,
                    path: normalizedPath,
                    roleId: item.roleId || item.role_id || group.roleId,
                    icon: ICON_MAP[item.name] || ICON_MAP[groupName] || LayoutDashboard,
                    children: [],
                    ...normalizePermissions(item)
                };
                pMap[normalizedPath] = node;
            });

            // Group presentation tree for Sidebar
            if (allItems.length === 0 || visibleItems.length === 0) {
                return;
            }

            // Single-item group (e.g. Dashboard)
            if (visibleItems.length === 1 && visibleItems[0].name.toLowerCase() === groupName.toLowerCase()) {
                const item = visibleItems[0];
                const path = item.slug || item.path || "";
                const normalizedPath = normalizePath(path);
                const node = {
                    id: item.id,
                    menuId: item.menuId || item.id,
                    text: item.name || groupName,
                    path: normalizedPath,
                    roleId: item.roleId || item.role_id || group.roleId,
                    icon: ICON_MAP[item.name] || ICON_MAP[groupName] || LayoutDashboard,
                    children: [],
                    ...normalizePermissions(item)
                };
                tree.push(node);
                pMap[normalizedPath] = node;
                return;
            }

            const children = visibleItems.map(item => {
                const name = item.name || "";
                const path = item.slug || item.path || "";
                const normalizedPath = normalizePath(path);
                const childNode = {
                    id: item.id,
                    menuId: item.menuId || item.id,
                    text: name,
                    path: normalizedPath,
                    roleId: item.roleId || item.role_id || group.roleId,
                    icon: ICON_MAP[name] || Blocks,
                    parentId: group.id,
                    ...normalizePermissions(item)
                };
                pMap[normalizedPath] = childNode;
                return childNode;
            });

            tree.push({
                id: group.id,
                menuId: group.id,
                text: groupName,
                path: null,
                icon: ICON_MAP[groupName] || Blocks,
                children,
                view: true
            });
        });
    } else {
        const menuMap = {};
        dynamicMenus.forEach(menu => {
            const name = menu.menuName || menu.name || "";
            const path = menu.slug || menu.path || "";
            const normalizedPath = normalizePath(path);
            const node = {
                id: menu.id,
                menuId: menu.menuId || menu.id,
                text: name,
                path: normalizedPath,
                roleId: menu.roleId || menu.role_id,
                icon: ICON_MAP[name] || Blocks,
                parentId: menu.parentId || menu.parent_id,
                children: [],
                ...normalizePermissions(menu)
            };
            menuMap[menu.id] = node;
            if (path) {
                pMap[normalizedPath] = node;
            }
        });

        dynamicMenus.forEach(menu => {
            const node = menuMap[menu.id];
            if (!node) return;
            const pId = node.parentId;
            if (pId && menuMap[pId]) {
                menuMap[pId].children.push(node);
                menuMap[pId].path = null;
            } else if (menu.view) {
                tree.push(node);
            }
        });
    }

    return { tree, pathMap: pMap };
};

// Match a given pathname against pathMap
export const findMenuItemForPath = (pathMap, pathname) => {
    if (!pathMap || Object.keys(pathMap).length === 0) return null;
    const currentPath = normalizePath(pathname);

    // 1. Direct exact match
    if (pathMap[currentPath]) {
        return pathMap[currentPath];
    }

    const paths = Object.keys(pathMap);

    // 2. Active path match (checks subpaths, stems, aliases)
    const activeMatch = paths.find(p => isPathActive(p, currentPath));
    if (activeMatch) {
        return pathMap[activeMatch];
    }

    // 3. Prefix match sorted by length descending
    const sortedPaths = paths.filter(p => p !== '/').sort((a, b) => b.length - a.length);
    const prefixMatch = sortedPaths.find(p => currentPath.startsWith(`${p}/`));
    if (prefixMatch) {
        return pathMap[prefixMatch];
    }

    return null;
};
